import { Tooltip, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { Diamond12Filled, Flag16Filled } from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { useDataTableStyles } from "@/components/data-table";
import { formatDate } from "@/lib/date-format";
import type { HomeGate } from "@/features/home/home-model";

const KIND_LABEL: Record<HomeGate["kind"], string> = { milestone: "마일스톤", deadline: "마감" };

const useStyles = makeStyles({
  table: { tableLayout: "fixed" },
  row: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  clipCell: { overflow: "hidden", maxWidth: 0 },
  nameCol: { width: "36%" },
  planCol: { width: "28%" },
  kindCol: { width: "18%" },
  dateCol: { width: "18%" },
  kind: { display: "inline-flex", alignItems: "center", gap: "6px", minWidth: 0 },
  mark: { fontSize: "12px", flexShrink: 0 },
  done: { color: tokens.colorNeutralForeground3 },
  late: { color: tokens.colorPaletteRedForeground1 },
});

function gateLabel(gate: HomeGate): string {
  return `${gate.title}, ${gate.projectName}, ${KIND_LABEL[gate.kind]}, ${formatDate(gate.date, "full")}`;
}

export function HomeGateTable({ gates, onOpen }: { gates: HomeGate[]; onOpen?: (gate: HomeGate) => void }) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const navigate = useNavigate();
  const openGate = (gate: HomeGate) => {
    if (onOpen) {
      onOpen(gate);
      return;
    }
    navigate(`/projects/${gate.projectId}/calendar?date=${gate.date}`);
  };

  return (
    <div className={table.frame}>
      <table className={mergeClasses(table.table, styles.table)} aria-label="주요 일정">
        <thead>
          <tr>
            <th className={mergeClasses(table.th, styles.nameCol)} scope="col">이름</th>
            <th className={mergeClasses(table.th, styles.planCol)} scope="col">플랜</th>
            <th className={mergeClasses(table.th, styles.kindCol)} scope="col">구분</th>
            <th className={mergeClasses(table.th, table.num, styles.dateCol)} scope="col">날짜</th>
          </tr>
        </thead>
        <tbody>
          {gates.map((gate) => {
            const iconColor = gate.done
              ? tokens.colorNeutralForeground3
              : gate.late || gate.kind === "deadline"
                ? tokens.colorPaletteRedForeground1
                : tokens.colorPaletteTealForeground2;
            return (
              <tr key={gate.key} className={styles.row} onClick={() => openGate(gate)}>
                <td className={mergeClasses(table.td, styles.clipCell, gate.done && styles.done)}>
                  <Tooltip content={gate.title} relationship="description">
                    <button
                      type="button"
                      className={mergeClasses(table.linkBtn, gate.done && styles.done)}
                      aria-label={gateLabel(gate)}
                      onClick={(event) => {
                        event.stopPropagation();
                        openGate(gate);
                      }}
                    >
                      {gate.title}
                    </button>
                  </Tooltip>
                </td>
                <td className={mergeClasses(table.td, styles.clipCell)}>
                  <Tooltip content={gate.projectName} relationship="description">
                    <span className={table.clip}>{gate.projectName}</span>
                  </Tooltip>
                </td>
                <td className={mergeClasses(table.td, gate.done && styles.done)}>
                  <span className={styles.kind}>
                    {gate.kind === "milestone" ? (
                      <Diamond12Filled className={styles.mark} style={{ color: iconColor }} />
                    ) : (
                      <Flag16Filled className={styles.mark} style={{ color: iconColor }} />
                    )}
                    {KIND_LABEL[gate.kind]}
                  </span>
                </td>
                <td className={mergeClasses(table.td, table.num, gate.done && styles.done, gate.late && styles.late)}>{formatDate(gate.date, "numeric")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
